import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CryptoTutorial = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [copiedSolana, setCopiedSolana] = useState(false);
  const [copiedEvm, setCopiedEvm] = useState(false);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'solana') {
      setCopiedSolana(true);
      setTimeout(() => setCopiedSolana(false), 2000);
    } else {
      setCopiedEvm(true);
      setTimeout(() => setCopiedEvm(false), 2000);
    }
  };

  // Crypto.com referral link and code
  const CRYPTO_COM_REFERRAL_LINK = "https://crypto.com/app/7vgawxhreh";
  const CRYPTO_COM_REFERRAL_CODE = "7vgawxhreh";

  const steps = [
    {
      number: 1,
      title: "Create a Crypto.com Account",
      icon: "📱",
      description: "Download the Crypto.com app and create your account. Use our referral link for bonus rewards!",
      action: (
        <div>
          <a 
            href={CRYPTO_COM_REFERRAL_LINK} 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn btn-primary"
            style={{display: 'inline-block', marginTop: '1rem'}}
          >
            🎁 Sign Up with Referral Link
          </a>
          <div style={{marginTop: '1rem', padding: '0.75rem', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem'}}>
            <p style={{color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.25rem'}}>
              When prompted for invite/referral code, enter:
            </p>
            <p style={{color: '#8b5cf6', fontWeight: 'bold', fontSize: '1.1rem', fontFamily: 'monospace', marginBottom: '0'}}>
              {CRYPTO_COM_REFERRAL_CODE}
            </p>
          </div>
        </div>
      )
    },
    {
      number: 2,
      title: "Complete Verification",
      icon: "✅",
      description: "Complete the KYC (Know Your Customer) verification process. This usually takes a few minutes to a couple of hours. You'll need a valid ID and possibly a selfie.",
      tips: ["Have your ID ready", "Ensure good lighting for photos", "Most verifications complete within 24 hours"]
    },
    {
      number: 3,
      title: "Buy USDT/USDC",
      icon: "💵",
      description: "Once verified, buy USDT or USDC using your credit/debit card or bank transfer. These are stablecoins pegged to the US Dollar.",
      tips: ["USDT and USDC are both valued at ~$1 each", "Buy slightly more than needed for transaction fees", "Compare fees between payment methods"]
    },
    {
      number: 4,
      title: "Set Up Your Personal Wallet",
      icon: "👛",
      description: "For Solana: Download Phantom Wallet (phantom.app). For EVM chains: Download MetaMask (metamask.io). Create a new wallet and SAVE YOUR SEED PHRASE!",
      tips: ["Never share your seed phrase with anyone", "Write it down on paper, not digitally", "You can use Phantom for Solana or MetaMask for EVM networks"]
    },
    {
      number: 5,
      title: "Withdraw to Your Wallet",
      icon: "📤",
      description: "In Crypto.com, go to Transfer → Withdraw → External Wallet. Select USDT/USDC, paste your wallet address, select the network (Solana or EVM), and confirm.",
      tips: ["Double-check the wallet address", "Select the correct network (Solana or EVM)", "Start with a small test amount first"]
    },
    {
      number: 6,
      title: "Pay for Bitarot Subscription",
      icon: "🔮",
      description: "Once funds arrive in your wallet, send the subscription amount to our wallet address. Copy the address from our Subscription page and use your wallet app to send.",
      action: (
        <button 
          onClick={() => navigate('/subscription')}
          className="btn btn-primary"
          style={{display: 'inline-block', marginTop: '1rem'}}
        >
          💳 Go to Subscription Page
        </button>
      )
    },
    {
      number: 7,
      title: "Submit Payment Confirmation",
      icon: "📝",
      description: "After sending, go to our Subscription page, select your tier, and submit your payment details. Include your wallet address and transaction hash if available. Our admin will verify and activate your subscription within 24 hours.",
      tips: ["Save the transaction hash for reference", "Your subscription will be activated after verification"]
    }
  ];

  return (
    <div className="container fade-in" data-testid="crypto-tutorial">
      <div className="mystical-bg"></div>
      <h1 className="text-center mb-4 glow-text" style={{fontSize: '3rem'}}>
        🎓 Crypto Payment Guide
      </h1>
      
      <p className="text-center" style={{color: '#cbd5e1', maxWidth: '700px', margin: '0 auto 3rem', lineHeight: '1.8'}}>
        New to crypto? No worries! Follow this simple guide to set up your wallet and pay for your Bitarot subscription. 
        It's easier than you think! 💫
      </p>

      <div style={{maxWidth: '900px', margin: '0 auto'}}>
        {steps.map((step, idx) => (
          <div key={idx} className="card" style={{marginBottom: '1.5rem', position: 'relative'}}>
            {/* Step Number Badge */}
            <div style={{
              position: 'absolute',
              top: '-15px',
              left: '20px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
            }}>
              {step.number}
            </div>

            <div style={{paddingLeft: '2rem'}}>
              <h3 style={{fontSize: '1.5rem', marginBottom: '1rem', color: '#e0e7ff'}}>
                {step.icon} {step.title}
              </h3>
              
              <p style={{color: '#cbd5e1', lineHeight: '1.7', marginBottom: step.tips ? '1rem' : '0'}}>
                {step.description}
              </p>

              {step.tips && (
                <ul style={{listStyle: 'none', padding: 0, marginTop: '1rem'}}>
                  {step.tips.map((tip, tipIdx) => (
                    <li key={tipIdx} style={{
                      color: '#9ca3af',
                      fontSize: '0.9rem',
                      marginBottom: '0.5rem',
                      paddingLeft: '1.5rem',
                      position: 'relative'
                    }}>
                      <span style={{position: 'absolute', left: 0}}>💡</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              )}

              {step.action}
            </div>
          </div>
        ))}
      </div>

      {/* Wallet Addresses Quick Reference */}
      <div className="card" style={{maxWidth: '900px', margin: '3rem auto 0', background: 'rgba(139, 92, 246, 0.1)'}}>
        <h3 style={{fontSize: '1.5rem', marginBottom: '1.5rem', color: '#8b5cf6'}}>
          📋 Quick Reference: Our Wallet Addresses
        </h3>
        
        <div style={{marginBottom: '1.5rem'}}>
          <p style={{fontWeight: 'bold', color: '#22c55e', marginBottom: '0.5rem'}}>
            Solana (USDC/USDT):
          </p>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <code style={{
              flex: 1,
              padding: '1rem',
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: '0.5rem',
              wordBreak: 'break-all',
              color: '#cbd5e1',
              fontSize: '0.9rem'
            }}>
              FDR26YGZWRA2x3XngAa2pj65cfPMSqkpYVkMNyqWNxy9
            </code>
            <button
              onClick={() => copyToClipboard('FDR26YGZWRA2x3XngAa2pj65cfPMSqkpYVkMNyqWNxy9', 'solana')}
              style={{
                background: copiedSolana ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #22c55e, #16a34a)',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                transition: 'all 0.3s ease',
                opacity: copiedSolana ? 1 : 0.9
              }}
            >
              {copiedSolana ? '✅ Copied!' : '📋 Copy'}
            </button>
          </div>
        </div>

        <div>
          <p style={{fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.5rem'}}>
            EVM Networks (Ethereum, Base, Polygon, BSC, etc.) - USDT:
          </p>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <code style={{
              flex: 1,
              padding: '1rem',
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: '0.5rem',
              wordBreak: 'break-all',
              color: '#cbd5e1',
              fontSize: '0.9rem'
            }}>
              0x56F45321Ff47B3f087aD92a37aE67237A1905018
            </code>
            <button
              onClick={() => copyToClipboard('0x56F45321Ff47B3f087aD92a37aE67237A1905018', 'evm')}
              style={{
                background: copiedEvm ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                transition: 'all 0.3s ease'
              }}
            >
              {copiedEvm ? '✅ Copied!' : '📋 Copy'}
            </button>
          </div>
        </div>
      </div>

      {/* WISE Payment Option */}
      <div className="card" style={{maxWidth: '900px', margin: '2rem auto 0', background: 'rgba(34, 197, 94, 0.1)'}}>
        <h3 style={{fontSize: '1.5rem', marginBottom: '1.5rem', color: '#22c55e'}}>
          💵 Prefer Cash/USD Payment?
        </h3>
        <p style={{color: '#cbd5e1', marginBottom: '1.5rem'}}>
          You can also pay via <strong>WISE App</strong> in USD. The amount is equivalent to USDT value (e.g., 20 USDT = $20 USD).
        </p>
        
        {/* QR Code */}
        <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
          <img 
            src="https://customer-assets.emergentagent.com/job_f762d5c9-eb11-408d-adfe-a6974644b398/artifacts/wq86fhkv_Screenshot%202026-01-06%20at%2012.22.10%E2%80%AFAM.png"
            alt="WISE Payment QR Code"
            style={{maxWidth: '180px', borderRadius: '0.5rem', border: '2px solid rgba(34, 197, 94, 0.3)'}}
          />
        </div>
        
        {/* WISE Link */}
        <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
          <a 
            href="https://wise.com/pay/me/kelvint17" 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn btn-primary"
            style={{display: 'inline-block', background: 'linear-gradient(135deg, #22c55e, #16a34a)'}}
          >
            🔗 Pay via WISE Link
          </a>
        </div>
        
        {/* Important Note */}
        <div style={{padding: '1rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.5rem', border: '1px solid rgba(245, 158, 11, 0.3)'}}>
          <p style={{color: '#f59e0b', fontWeight: 'bold', marginBottom: '0.5rem'}}>⚠️ Important:</p>
          <p style={{color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '0'}}>
            Take a <strong>screenshot</strong> of your payment receipt with the <strong>Transaction ID visible</strong>. 
            Keep this as proof of payment in case any issues arise.
          </p>
        </div>
      </div>

      {/* Contact Info */}
      <div className="card" style={{maxWidth: '900px', margin: '2rem auto 0', background: 'rgba(139, 92, 246, 0.1)'}}>
        <h3 style={{fontSize: '1.25rem', marginBottom: '1rem', color: '#8b5cf6'}}>
          📧 Need Help?
        </h3>
        <p style={{color: '#cbd5e1', marginBottom: '0'}}>
          For any questions, issues, or payment verification, contact us at:{' '}
          <a href="mailto:NinjasOfSatoshi@proton.me" style={{color: '#8b5cf6', fontWeight: 'bold'}}>
            NinjasOfSatoshi@proton.me
          </a>
        </p>
      </div>

      {/* CTA */}
      <div className="text-center" style={{marginTop: '3rem'}}>
        {isAuthenticated ? (
          <button 
            onClick={() => navigate('/subscription')} 
            className="btn btn-primary"
            style={{fontSize: '1.2rem', padding: '1rem 2rem'}}
          >
            💎 View Subscription Plans
          </button>
        ) : (
          <button 
            onClick={() => navigate('/')} 
            className="btn btn-primary"
            style={{fontSize: '1.2rem', padding: '1rem 2rem'}}
          >
            🚀 Get Started with Bitarot
          </button>
        )}
      </div>
    </div>
  );
};

export default CryptoTutorial;
